import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { notifySnackbar } from "../../lib/notifySnackbar";
import useTranslation from "../../hooks/useTranslation";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  useRabbitHoleCurrent,
  useRabbitHoleCreateWeek,
  useRabbitHoleReport,
  useRabbitHoleAbandon,
  useRabbitHoleRefreshTopicSuggestion,
  useRabbitHoleSetNextTopic,
  useRabbitHoleTopicSuggestions,
  useRabbitHoleWeekDetail,
  type RabbitHoleDiscovery,
  type RabbitHoleTopicSuggestion,
} from "../../hooks/useRabbitHole";
import { usePaginatedVocabularyList } from "../../hooks/usePaginatedVocabularyList";
import { WordCard } from "../../components/WordCard/WordCard";
import { CONTINUE_READING_CTA_BY_LANGUAGE, NATIVE_LANGUAGE_LABELS, READ_TODAY_CTA_BY_LANGUAGE, TEXT_LANGUAGES } from "../../constants";
import {
  getResolvedGeneratedTextTitleCacheKey,
  type ResolvedGeneratedTextTitleCache,
} from "../../utils/generatedTextResolvedTitleCache";
import { isRabbitHoleDiscoveryRead } from "../../utils/rabbitHoleDiscoveryState";
import {
  getRabbitHoleTopicRefinement,
  fetchWikipediaRefinement,
  type RabbitHoleTopicRefinement,
} from "../../utils/rabbitHoleTopicRefinement";
import {
  RabbitHoleEditionCover,
  RabbitHoleRecommendationCover,
} from "../../components/RabbitHoleEditionCover";
import {
  formatRabbitHoleEditionDateRange,
  getRabbitHoleEditionTitle,
} from "../../utils/rabbitHoleEditionDesign";
import {
  resolveRabbitHoleRecommendedSuggestion,
} from "../../utils/rabbitHoleRecommendations";

function getLanguageNativeLabel(code: string): string {
  const nativeLabel = NATIVE_LANGUAGE_LABELS[code];
  if (nativeLabel) {
    return nativeLabel;
  }
  const lang = TEXT_LANGUAGES.find((l) => l.id === code);
  return lang?.pretty ?? code;
}

export const Route = createFileRoute("/app/rabbit_hole")({
  validateSearch: (search: Record<string, unknown>) => ({
    summary:
      search.summary === true ||
      search.summary === "1" ||
      search.summary === 1 ||
      search.summary === "true"
        ? true
        : undefined,
    weekId:
      typeof search.weekId === "number"
        ? search.weekId
        : typeof search.weekId === "string"
          ? Number(search.weekId)
          : undefined,
  }),
  component: RabbitHolePage,
});

const openingDiscoveryIds = new Set<number>();

function RabbitHolePage() {
  const { translate, language } = useTranslation();
  const navigate = useNavigate();
  const { weekId, summary } = Route.useSearch();
  const { languageSpace, setLanguageSpace } = useLanguageSpace("ENGLISH");
  const currentWeek = useRabbitHoleCurrent(languageSpace);
  const detailWeek = useRabbitHoleWeekDetail(
    weekId && weekId > 0 ? weekId : null,
  );
  const summaryReport = useRabbitHoleReport(
    weekId && weekId > 0 ? weekId : null,
  );
  const createWeek = useRabbitHoleCreateWeek();
  const abandonWeek = useRabbitHoleAbandon();
  const refreshTopicSuggestion = useRabbitHoleRefreshTopicSuggestion();
  const topicSuggestions = useRabbitHoleTopicSuggestions(languageSpace);
  const loginStatus = useLoginStatus();
  const [searchText, setSearchText] = useState("");
  const [resolvedTitleCache, setResolvedTitleCache] =
    useLocalStorage<ResolvedGeneratedTextTitleCache>(
      getResolvedGeneratedTextTitleCacheKey(
        loginStatus.data?.state === "logged in" ? loginStatus.data.userId : null,
      ),
      {},
    );


  const isViewingSpecificWeek = weekId !== undefined && weekId > 0;

  useEffect(() => {
    if (detailWeek.data?.language) {
      setLanguageSpace(detailWeek.data.language as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN");
    }
  }, [detailWeek.data?.language, setLanguageSpace]);

  if (isViewingSpecificWeek) {
    if (detailWeek.isLoading) {
      return (
        <main className="min-h-full bg-white" data-testid="rabbit-hole-page">
          <div className="mx-auto w-full max-w-2xl px-4 py-8 pb-24 md:px-8 md:py-12">
            <p className="text-base text-slate-500 animate-pulse">
              {translate("rabbit_hole_loading")}
            </p>
          </div>
        </main>
      );
    }

    const week = detailWeek.data;
    if (!week || !week.id) {
      return (
        <main className="min-h-full bg-white" data-testid="rabbit-hole-page">
          <div className="mx-auto w-full max-w-2xl px-4 py-8 pb-24 md:px-8 md:py-12">
            <p className="text-base text-slate-500">
              {translate("rabbit_hole_loading")}
            </p>
          </div>
        </main>
      );
    }

    if (summary) {
      return (
        <main className="min-h-full bg-white" data-testid="rabbit-hole-page">
          <div className="mx-auto w-full max-w-6xl px-4 py-8 pb-24 md:px-8 md:py-12">
            <WeekCompleteState
              report={summaryReport.data ?? {
                topic: week.topic,
                language: week.language,
                week_start: week.week_start,
                week_end: week.week_end,
                completed_discoveries: week.completed_discoveries ?? 0,
                read_discoveries: week.read_discoveries ?? 0,
                total_discoveries: week.total_discoveries ?? 6,
                vocabulary_count: 0,
                review_due_count: 0,
              }}
              translate={translate}
              weekId={week.id as number}
              navigate={navigate}
              nextWeekTopic={week.next_week_topic}
              discoveries={week.discoveries ?? []}
              statusLabel={
                week.status === "COMPLETED"
                  ? undefined
                  : translate("rabbit_hole_weekly_review")
              }
            />
          </div>
        </main>
      );
    }

    return (
      <main className="min-h-full bg-white" data-testid="rabbit-hole-page">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 pb-24 md:px-8 md:py-12">
          <ActiveWeekState
            week={week}
            abandonWeek={abandonWeek}
            navigate={navigate}
            translate={translate}
            appLanguage={language}
            resolvedTitleCache={resolvedTitleCache}
            setResolvedTitleCache={setResolvedTitleCache}
            readOnly={week.status === "COMPLETED"}
          />
        </div>
      </main>
    );
  }

  if (currentWeek.isLoading) {
    return (
      <main className="min-h-full bg-white" data-testid="rabbit-hole-page">
        <div className="mx-auto w-full max-w-2xl px-4 py-8 pb-24 md:px-8 md:py-12">
          <p className="text-base text-slate-500 animate-pulse">
            {translate("rabbit_hole_loading")}
          </p>
        </div>
      </main>
    );
  }

  const week = currentWeek.data;
  const isPlanningState = !week || week.status === "none" || week.status === "PLANNING";
  const isPlanningRecommendationLoading =
    topicSuggestions.isLoading ||
    (topicSuggestions.data?.length ?? 0) === 0 && refreshTopicSuggestion.isPending;

  return (
    <main className="min-h-full bg-white" data-testid="rabbit-hole-page">
      <div
        className={`mx-auto w-full px-4 py-8 pb-24 md:px-8 md:py-12 ${isPlanningState ? "max-w-2xl" : "max-w-6xl"}`}
      >
        {isPlanningState ? (
          <PlanningState
            searchText={searchText}
            setSearchText={setSearchText}
            languageSpace={languageSpace}
            appLanguage={language}
            createWeek={createWeek}
            translate={translate}
            suggestions={topicSuggestions.data ?? []}
            isLoading={isPlanningRecommendationLoading}
            refreshTopicSuggestion={refreshTopicSuggestion}
            navigate={navigate}
          />
        ) : (
          <ActiveWeekState
            week={week}
            abandonWeek={abandonWeek}
            navigate={navigate}
            translate={translate}
            appLanguage={language}
            resolvedTitleCache={resolvedTitleCache}
            setResolvedTitleCache={setResolvedTitleCache}
          />
        )}
      </div>
    </main>
  );
}

export function PlanningState({
  searchText,
  setSearchText,
  languageSpace,
  appLanguage,
  createWeek,
  translate,
  suggestions,
  isLoading,
  refreshTopicSuggestion,
  navigate,
}: {
  searchText: string;
  setSearchText: (v: string) => void;
  languageSpace: string;
  appLanguage: string;
  createWeek: ReturnType<typeof useRabbitHoleCreateWeek>;
  translate: (key: string, vars?: Record<string, string | number>) => string;
  suggestions: RabbitHoleTopicSuggestion[];
  isLoading: boolean;
  refreshTopicSuggestion: ReturnType<typeof useRabbitHoleRefreshTopicSuggestion>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [pendingClarificationTopic, setPendingClarificationTopic] = useState<RabbitHoleTopicRefinement | null>(null);
  const [isCheckingRefinement, setIsCheckingRefinement] = useState(false);
  const recommendedSuggestion = resolveRabbitHoleRecommendedSuggestion({
    suggestions,
  });
  const recommendedTopic = recommendedSuggestion?.topic ?? null;

  const handleTopicSelect = async (topic: string, suggestionId?: number) => {
    try {
      await createWeek.mutateAsync({
        topic,
        language: languageSpace,
        topic_source: typeof suggestionId === "number" ? "IDEA" : "MANUAL",
        topic_suggestion_id: suggestionId,
      });
    } catch {
      notifySnackbar({
        message:
          typeof navigator !== "undefined" && !navigator.onLine
            ? translate("vocabulary_add_offline_error")
            : translate("rabbit_hole_create_error"),
      });
    }
  };

  useEffect(() => {
    if (isLoading || recommendedTopic || refreshTopicSuggestion.isPending) {
      return;
    }
    if (suggestions.length > 0) {
      return;
    }
    refreshTopicSuggestion.mutate({ language: languageSpace });
  }, [isLoading, languageSpace, recommendedTopic, refreshTopicSuggestion, suggestions.length]);

  const handleFreeTypeSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = searchText.trim();
    if (!trimmed) return;
    const refinement = getRabbitHoleTopicRefinement(trimmed);
    if (refinement) {
      setPendingClarificationTopic(refinement);
      return;
    }
    setIsCheckingRefinement(true);
    fetchWikipediaRefinement(trimmed).then((wikipediaRefinement) => {
      setIsCheckingRefinement(false);
      if (wikipediaRefinement) {
        setPendingClarificationTopic(wikipediaRefinement);
        return;
      }
      handleTopicSelect(trimmed);
    }).catch(() => {
      setIsCheckingRefinement(false);
      handleTopicSelect(trimmed);
    });
  };

  const clarification =
    pendingClarificationTopic && pendingClarificationTopic.topic === searchText.trim()
      ? pendingClarificationTopic
      : null;

  if (createWeek.isPending) {
    const previewStart = new Date();
    const previewEnd = new Date(previewStart);
    previewEnd.setDate(previewEnd.getDate() + 6);
    const weekStart = formatIsoDate(previewStart);
    const weekEnd = formatIsoDate(previewEnd);
    const previewLanguage = createWeek.variables?.language || languageSpace;
    const previewTopic = createWeek.variables?.topic || searchText.trim();
    return (
      <RabbitHoleEditionCover
        topic={previewTopic}
        title=""
        language={previewLanguage}
        languageLabel={getLanguageNativeLabel(previewLanguage)}
        weekStart={weekStart}
        weekEnd={weekEnd}
        dayIndex={0}
        completed={0}
        total={6}
        dayLabel={translate("rabbit_hole_edition_day", { day: 1 })}
        progressLabel={translate("rabbit_hole_preparing")}
        verticalLabel={translate("rabbit_hole_edition_vertical_label")}
        dateRangeLabel={formatRabbitHoleEditionDateRange(weekStart, weekEnd, appLanguage)}
        titlePending
        actionLabel={READ_TODAY_CTA_BY_LANGUAGE[previewLanguage] ?? translate("rabbit_hole_read_today")}
        testId="rabbit-hole-edition-preview"
      />
    );
  }

  if (!recommendedTopic) {
    return (
      <div className="mx-auto max-w-[31rem]">
        <p className="text-base text-slate-600">
          {isLoading
            ? translate("rabbit_hole_recommendations_loading")
            : translate("rabbit_hole_recommendations_unavailable")}
        </p>
        {!isLoading ? (
          <button
            type="button"
            onClick={() => refreshTopicSuggestion.mutate({ language: languageSpace })}
            className="mt-4 min-h-12 rounded-[5px] border border-slate-950 bg-slate-950 px-5 text-sm font-medium text-white transition hover:border-slate-800 hover:bg-slate-800 disabled:cursor-wait disabled:border-slate-300 disabled:bg-slate-300 disabled:text-slate-500"
          >
            {translate("rabbit_hole_recommendations_retry")}
          </button>
        ) : null}
      </div>
    );
  }

  const recommendationWeekStart = formatIsoDate(new Date());

  return (
    <div className="mx-auto max-w-[31rem]">
      <header>
        <p className="text-sm font-medium tracking-[0.22em] text-slate-600">
          {getLanguageNativeLabel(languageSpace)}
        </p>
        <h1 className="mt-3 font-serif text-[2.45rem] leading-[1.02] tracking-[-0.025em] text-slate-950 sm:text-[3rem]">
          {translate("rabbit_hole_next_edition_title")}
        </h1>
      </header>

      <section className="mt-7" aria-labelledby="rabbit-hole-recommendation-title">
        <RabbitHoleRecommendationCover
          topic={recommendedTopic}
          language={languageSpace}
          weekStart={recommendationWeekStart}
          verticalLabel={translate("rabbit_hole_edition_vertical_label")}
          recommendationLabel={getLanguageNativeLabel(languageSpace)}
          testId="rabbit-hole-recommendation"
        />

        <button
          type="button"
          onClick={() =>
            handleTopicSelect(recommendedTopic, recommendedSuggestion?.id)
          }
          className="mt-5 flex min-h-14 w-full items-center justify-between rounded-[5px] border border-slate-950 bg-slate-950 px-5 text-left text-base font-medium text-white transition hover:border-slate-800 hover:bg-slate-800 disabled:cursor-wait disabled:border-slate-300 disabled:bg-slate-300 disabled:text-slate-500"
          data-testid="rabbit-hole-recommendation-start"
        >
          <span>{translate("rabbit_hole_choose_recommended")}</span>
          <ArrowRightIcon className="h-6 w-6" aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={() => refreshTopicSuggestion.mutate({ language: languageSpace })}
          disabled={refreshTopicSuggestion.isPending}
          className="mt-3 flex min-h-12 w-full items-center justify-between rounded-[5px] border border-slate-200 bg-slate-50 px-5 text-left text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-white hover:text-slate-950 disabled:cursor-default disabled:opacity-50"
          data-testid="rabbit-hole-another-recommendation"
        >
          <span>{translate("rabbit_hole_another_recommendation")}</span>
          <ArrowPathIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        </button>
      </section>

      <section className="mt-5 border-t border-slate-200 pt-6">
        <form onSubmit={handleFreeTypeSubmit}>
          <div className="flex min-h-14 items-center rounded-[5px] border border-slate-300 bg-white focus-within:border-slate-500">
            <input
              type="text"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPendingClarificationTopic(null);
              }}
              placeholder={translate("rabbit_hole_custom_topic_placeholder")}
              className="min-w-0 flex-1 bg-transparent px-5 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
              data-testid="rabbit-hole-topic-input"
            />
            <button
              type="submit"
              disabled={isCheckingRefinement || !searchText.trim()}
              className="flex h-12 w-14 shrink-0 items-center justify-center text-slate-700 transition hover:text-slate-950 disabled:text-slate-300"
              aria-label={translate("rabbit_hole_custom_topic_submit")}
              data-testid="rabbit-hole-topic-submit"
            >
              {isCheckingRefinement ? (
                <span aria-hidden="true">…</span>
              ) : (
                <ArrowRightIcon className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </form>

        {clarification ? (
          <div className="mt-4">
            <TopicRefinementCard
              refinement={clarification}
              translate={translate}
              onChooseTopic={handleTopicSelect}
              onKeepBroad={() => handleTopicSelect(clarification.topic)}
              testIdPrefix="rabbit-hole-planning"
            />
          </div>
        ) : null}
      </section>

      <div className="mt-8 border-t border-slate-100 pt-6">
        <button
          type="button"
          onClick={() => void navigate({ to: "/app" })}
          className="text-sm text-slate-500 underline underline-offset-4 hover:text-slate-700 transition"
          data-testid="rabbit-hole-back-to-overview"
        >
          {translate("rabbit_hole_back_to_overview")}
        </button>
      </div>
    </div>
  );
}

function ActiveWeekState({
  week,
  abandonWeek,
  navigate,
  translate,
  appLanguage,
  resolvedTitleCache,
  setResolvedTitleCache,
  readOnly,
}: {
  week: NonNullable<ReturnType<typeof useRabbitHoleCurrent>["data"]>;
  abandonWeek: ReturnType<typeof useRabbitHoleAbandon>;
  navigate: ReturnType<typeof useNavigate>;
  translate: (key: string, vars?: Record<string, string | number>) => string;
  appLanguage: string;
  resolvedTitleCache: ResolvedGeneratedTextTitleCache;
  setResolvedTitleCache: (
    value:
      | ResolvedGeneratedTextTitleCache
      | ((prev: ResolvedGeneratedTextTitleCache) => ResolvedGeneratedTextTitleCache),
  ) => void;
  readOnly?: boolean;
}) {
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);

  const report = useRabbitHoleReport(typeof week.id === "number" ? week.id : null);

  const handleOpenDiscovery = async (discovery: {
    id: number;
    status: string;
    topic?: string | null;
  }) => {
    if (openingDiscoveryIds.has(discovery.id)) {
      return;
    }
    openingDiscoveryIds.add(discovery.id);
    const effectiveDiscovery = week.discoveries?.find((entry) => entry.id === discovery.id);
    const visibleTitle = (discovery.topic ?? "").trim();
    if (
      visibleTitle &&
      !visibleTitle.toLowerCase().startsWith("rabbit hole") &&
      effectiveDiscovery?.status === "PENDING" &&
      resolvedTitleCache[String(discovery.id)] !== visibleTitle
    ) {
      setResolvedTitleCache({
        ...resolvedTitleCache,
        [String(discovery.id)]: visibleTitle,
      });
    }
    try {
      await navigate({
        to: "/app/reeprise/$generatedTextId",
        params: { generatedTextId: String(discovery.id) },
        search: {
          chapter: undefined,
          focusSentence: undefined,
          focusToken: undefined,
          focusWord: undefined,
          jumpTo: undefined,
        },
      });
    } finally {
      openingDiscoveryIds.delete(discovery.id);
    }
  };

  const weekStart = week.week_start ? new Date(week.week_start + "T00:00:00") : new Date();
  const todayIso = formatIsoDate(new Date());
  const dateLocale = appLanguage === "GERMAN" ? "de-DE" : "en-US";
  const dateFormat = new Intl.DateTimeFormat(dateLocale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const discoverySlots = Array.from({ length: 6 }, (_, i) => {
    const existing = week.discoveries?.find((d) => d.rabbit_hole_day_index === i);
    const slotDate = new Date(weekStart);
    slotDate.setDate(slotDate.getDate() + i);
    const slotIso = formatIsoDate(slotDate);
    const isCompleted =
      existing && existing.status === "COMPLETE" && existing.has_content === true;
    const isToday = slotIso === todayIso;
    const isFuture = slotIso > todayIso;
    const titlePending = existing && (existing.topic || "").startsWith("rabbit hole");
    return {
      index: i,
      date: slotDate,
      existing,
      isCompleted,
      isToday,
      isFuture,
      titlePending,
    };
  });
  const todaySlot = discoverySlots.find((slot) => slot.isToday);
  const isArchivedWeek = week.status === "COMPLETED";
  const mainDayIndex = isArchivedWeek ? 6 : todaySlot?.index ?? 0;
  const calendarSlots = discoverySlots.filter((slot) => slot.index !== mainDayIndex);
  const archivedTitle = translate("rabbit_hole_complete");
  const todayTitle = getRabbitHoleEditionTitle(todaySlot?.existing?.topic);
  const hasReadDiscovery = (discovery: RabbitHoleDiscovery | undefined) =>
    isRabbitHoleDiscoveryRead(discovery);
  const readDiscoveries =
    week.read_discoveries ?? discoverySlots.filter((slot) => hasReadDiscovery(slot.existing)).length;
  const totalDiscoveries = week.total_discoveries ?? 6;
  const editionDateRange = formatRabbitHoleEditionDateRange(
    week.week_start,
    week.week_end,
    appLanguage,
  );
  const weekSummary = week.journey_plan?.why_this_is_interesting ?? "";

  if (
    !readOnly &&
    week.status !== "COMPLETED" &&
    report.data &&
    report.data.read_discoveries >= report.data.total_discoveries &&
    report.data.total_discoveries > 0
  ) {
    return (
      <WeekCompleteState report={report.data} translate={translate} weekId={week.id as number} navigate={navigate} nextWeekTopic={week.next_week_topic} discoveries={week.discoveries ?? []} />
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-10">
        <RabbitHoleEditionCover
          topic={week.topic}
          title={isArchivedWeek ? archivedTitle : todayTitle.title}
          language={week.language}
          languageLabel={getLanguageNativeLabel(week.language)}
          weekStart={week.week_start}
          weekEnd={week.week_end}
          dayIndex={mainDayIndex}
          completed={readDiscoveries}
          total={totalDiscoveries}
          dayLabel={translate("rabbit_hole_edition_day", {
            day: mainDayIndex + 1,
          })}
          progressLabel={translate("rabbit_hole_progress", {
            completed: readDiscoveries,
            total: totalDiscoveries,
          })}
          verticalLabel={translate("rabbit_hole_edition_vertical_label")}
          dateRangeLabel={editionDateRange}
          isRead={
            isArchivedWeek
              ? false
              : hasReadDiscovery(todaySlot?.existing)
          }
          titlePending={isArchivedWeek ? false : todayTitle.pending}
          unavailableLabel={
            isArchivedWeek
              ? translate("rabbit_hole_complete")
              : translate("overview_rabbit_hole_no_today_title")
          }
          actionLabel={
            isArchivedWeek
              ? translate("rabbit_hole_open_summary")
              : READ_TODAY_CTA_BY_LANGUAGE[week.language] ?? translate("rabbit_hole_read_today")
          }
          readActionLabel={
            isArchivedWeek
              ? undefined
              : CONTINUE_READING_CTA_BY_LANGUAGE[week.language] ?? translate("rabbit_hole_continue_reading")
          }
          onAction={
            isArchivedWeek
              ? () =>
                  void navigate({
                    to: "/app/rabbit_hole",
                    search: { weekId: week.id as number, summary: true },
                  })
              : todaySlot?.existing
                ? () => void handleOpenDiscovery(todaySlot.existing!)
                : undefined
          }
          testId="rabbit-hole-edition"
        />

        <section>
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">
                {translate("rabbit_hole_calendar_title")}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                {translate("rabbit_hole_calendar_body")}
              </p>
            </div>
          </div>

          <div
            className="mt-4 grid min-w-0 grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
            data-testid="rabbit-hole-calendar"
          >
            {calendarSlots.map((slot) => {
              const slotTitle = getRabbitHoleEditionTitle(slot.existing?.topic);
              const dateLabel = dateFormat.format(slot.date);
              return (
                <RabbitHoleEditionCover
                  key={slot.index}
                  topic={week.topic}
                  title={slotTitle.title}
                  language={week.language}
                  languageLabel={getLanguageNativeLabel(week.language)}
                  weekStart={week.week_start}
                  weekEnd={week.week_end}
                  dayIndex={slot.index}
                  completed={readDiscoveries}
                  total={totalDiscoveries}
                  dayLabel={translate("rabbit_hole_edition_day", {
                    day: slot.index + 1,
                  })}
                  progressLabel={dateLabel}
                  verticalLabel={translate("rabbit_hole_edition_vertical_label")}
                  dateRangeLabel={editionDateRange}
                  isRead={hasReadDiscovery(slot.existing)}
                  titlePending={slotTitle.pending}
                  unavailableLabel={translate("rabbit_hole_generating")}
                  onAction={
                    slot.existing && !slot.isFuture && !slotTitle.pending
                      ? () => void handleOpenDiscovery(slot.existing!)
                      : undefined
                  }
                  variant="mini"
                  testId={`rabbit-hole-calendar-day-${slot.index}`}
                />
              );
            })}
          </div>
        </section>
      </div>

      {!readOnly ? (
        <>
          <RabbitHoleWeekSummary
            summary={weekSummary}
            topic={week.topic}
          />

          <section className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm font-medium text-slate-700">
              {translate("rabbit_hole_manage_danger_title")}
            </p>
            <p className="mt-1 text-sm text-slate-500">{translate("rabbit_hole_manage_danger_body")}</p>
            <button
              type="button"
              onClick={() => setShowAbandonConfirm(true)}
              className="mt-4 rounded-lg border border-red-200 px-4 py-3 text-sm font-medium text-red-600 transition hover:bg-red-50"
              data-testid="rabbit-hole-abandon"
            >
              {translate("rabbit_hole_abandon")}
            </button>
          </section>

          {showAbandonConfirm && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4 py-8">
              <div className="w-full max-w-md rounded-2xl bg-white p-6">
                <h3 className="text-lg font-semibold text-slate-900">
                  {translate("rabbit_hole_abandon_confirm_title")}
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  {translate("rabbit_hole_abandon_confirm_body")}
                </p>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors disabled:opacity-50"
                    onClick={() => setShowAbandonConfirm(false)}
                    disabled={abandonWeek.isPending}
                  >
                    {translate("rabbit_hole_abandon_cancel")}
                  </button>
                  <button
                    type="button"
                    className="rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={async () => {
                      if (typeof week.id !== "number") return;
                      try {
                        await abandonWeek.mutateAsync({ weekId: week.id });
                      } catch {
                        // handled by mutation
                      }
                    }}
                    disabled={abandonWeek.isPending}
                    data-testid="rabbit-hole-abandon-confirm"
                  >
                    {abandonWeek.isPending
                      ? translate("rabbit_hole_abandon_pending")
                      : translate("rabbit_hole_abandon_confirm_button")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export function RabbitHoleWeekSummary({
  summary,
  topic,
}: {
  summary: string;
  topic: string;
}) {
  if (!summary.trim() && !topic.trim()) {
    return null;
  }

  return (
    <section data-testid="rabbit-hole-week-summary">
      {topic.trim() ? (
        <p className="text-lg font-semibold leading-tight text-slate-900">
          {topic}
        </p>
      ) : null}
      {summary.trim() ? (
        <p className="mt-2 text-sm leading-6 text-slate-600">
          {summary}
        </p>
      ) : null}
    </section>
  );
}

function WeekCompleteState({
  report,
  translate,
  weekId,
  navigate,
  statusLabel,
  nextWeekTopic,
  discoveries,
}: {
  report: NonNullable<ReturnType<typeof useRabbitHoleReport>["data"]>;
  translate: (key: string, vars?: Record<string, string | number>) => string;
  weekId: number;
  navigate: ReturnType<typeof useNavigate>;
  statusLabel?: string;
  nextWeekTopic: string;
  discoveries: RabbitHoleDiscovery[];
}) {
  const setNextTopic = useRabbitHoleSetNextTopic();
  const vocabularyList = usePaginatedVocabularyList({
    rabbit_hole_week_id: weekId,
  });
  const vocabularies = vocabularyList.data?.pages.flatMap((page) => page.results) ?? [];
  const isLoading = vocabularyList.isLoading;
  const [nextTopicDraft, setNextTopicDraft] = useState(nextWeekTopic);
  const [nextTopicSaved, setNextTopicSaved] = useState(false);
  const [chaptersExpanded, setChaptersExpanded] = useState(false);

  const handleNextTopicSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const topic = nextTopicDraft.trim();
    if (!topic || setNextTopic.isPending) return;
    try {
      await setNextTopic.mutateAsync({ weekId, topic });
      setNextTopicSaved(true);
    } catch {
      notifySnackbar({
        message:
          typeof navigator !== "undefined" && !navigator.onLine
            ? translate("vocabulary_add_offline_error")
            : translate("rabbit_hole_create_error"),
      });
    }
  };

  const handleOpenChapter = (discovery: RabbitHoleDiscovery) => {
    void navigate({
      to: "/app/reeprise/$generatedTextId",
      params: { generatedTextId: String(discovery.id) },
      search: {
        chapter: undefined,
        focusSentence: undefined,
        focusToken: undefined,
        focusWord: undefined,
        jumpTo: undefined,
      },
    });
  };

  const orderedDiscoveries = [...discoveries].sort(
    (a, b) => (a.rabbit_hole_day_index ?? 0) - (b.rabbit_hole_day_index ?? 0),
  );

  return (
    <div className="space-y-8" data-testid="rabbit-hole-complete-state">
      <div>
        <p className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
          {statusLabel ?? translate("rabbit_hole_complete")}
        </p>
        <h1 className="mt-2 font-serif text-2xl leading-tight tracking-[0.1px] text-slate-950">
          {report.topic}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {getLanguageNativeLabel(report.language)}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3" data-testid="rabbit-hole-complete-stats">
        <div className="rounded-xl border border-slate-200 px-3 py-2.5">
          <p className="text-lg font-semibold leading-none text-slate-900">
            {translate("rabbit_hole_recap_progress", {
              read: report.read_discoveries,
              total: report.total_discoveries,
            })}
          </p>
          <p className="mt-1.5 text-xs text-slate-500">{translate("rabbit_hole_discoveries_explored")}</p>
        </div>
        {report.review_due_count > 0 ? (
          <div className="rounded-xl border border-slate-200 px-3 py-2.5">
            <p className="text-lg font-semibold leading-none text-slate-900">{report.review_due_count}</p>
            <p className="mt-1.5 text-xs text-slate-500">{translate("rabbit_hole_review_due")}</p>
          </div>
        ) : null}
      </div>

      {orderedDiscoveries.length > 0 ? (
        <section data-testid="rabbit-hole-chapters">
          <button
            type="button"
            onClick={() => setChaptersExpanded((prev) => !prev)}
            aria-expanded={chaptersExpanded}
            className="flex w-full items-center justify-between gap-3 text-left"
            data-testid="rabbit-hole-chapters-toggle"
          >
            <span className="font-serif text-lg leading-tight text-slate-900">
              {translate("rabbit_hole_week_chapters_title")}
            </span>
            <span className="flex items-center gap-2 text-xs text-slate-500">
              {orderedDiscoveries.length}
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform ${chaptersExpanded ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </span>
          </button>

          {chaptersExpanded ? (
            <ul className="m-0 mt-3 w-full space-y-2 p-0">
              {orderedDiscoveries.map((discovery) => {
                const read = isRabbitHoleDiscoveryRead(discovery);
                const canRead = discovery.has_content === true;
                return (
                  <li key={discovery.id}>
                    <button
                      type="button"
                      onClick={() => canRead && handleOpenChapter(discovery)}
                      disabled={!canRead}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-default disabled:opacity-60"
                      data-testid={`rabbit-hole-chapter-${discovery.rabbit_hole_day_index ?? 0}`}
                    >
                      <span className="min-w-0">
                        <span className="block text-xs text-slate-400">
                          {translate("rabbit_hole_edition_day", {
                            day: (discovery.rabbit_hole_day_index ?? 0) + 1,
                          })}
                        </span>
                        <span className="mt-0.5 block truncate text-base font-medium text-slate-900">
                          {discovery.topic}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-3">
                        <span
                          className={
                            read
                              ? "text-xs text-slate-400"
                              : "text-xs font-medium text-slate-600"
                          }
                        >
                          {read
                            ? translate("rabbit_hole_chapter_read")
                            : translate("rabbit_hole_chapter_unread")}
                        </span>
                        {canRead ? (
                          <span className="flex items-center gap-1 text-sm font-medium text-slate-700">
                            {read
                              ? translate("rabbit_hole_continue_reading")
                              : translate("rabbit_hole_read_now")}
                            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </section>
      ) : null}

      <section>
        <h2 className="font-serif text-lg leading-tight text-slate-900">
          {translate("rabbit_hole_week_vocabulary_title", {
            count: report.vocabulary_count,
          })}
        </h2>

        {isLoading && (
          <p className="mt-3 text-base text-slate-500 animate-pulse">
            {translate("rabbit_hole_week_vocabulary_loading")}
          </p>
        )}

        {!isLoading && vocabularies.length === 0 && (
          <p className="mt-3 text-base text-slate-500">
            {translate("rabbit_hole_week_vocabulary_empty")}
          </p>
        )}

        {vocabularies.length > 0 && (
          <ul className="m-0 mt-3 w-full p-0">
            {vocabularies.map((voc, index) => (
              <WordCard
                key={voc.id}
                vocabulary={voc}
                hasTopDivider={index > 0}
                variant="plain"
              />
            ))}
          </ul>
        )}

        {vocabularyList.hasNextPage && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => vocabularyList.fetchNextPage()}
              className="rounded-lg border border-slate-300 px-4 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-50 transition"
              data-testid="rabbit-hole-vocabulary-load-more"
            >
              {translate("vocabulary_progress_load_more")}
            </button>
          </div>
        )}
      </section>

      <section className="rounded-[1.5rem] border border-slate-200 bg-slate-50/70 p-4" data-testid="rabbit-hole-next-edition">
        <p className="text-sm font-medium text-slate-900">
          {translate("rabbit_hole_next_topic_label")}
        </p>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
          {translate("rabbit_hole_next_topic_body")}
        </p>
        <form onSubmit={handleNextTopicSubmit} className="mt-4">
          <div className="flex min-h-12 items-center rounded-[5px] border border-slate-300 bg-white focus-within:border-slate-500">
            <input
              type="text"
              value={nextTopicDraft}
              onChange={(e) => {
                setNextTopicDraft(e.target.value);
                setNextTopicSaved(false);
              }}
              placeholder={translate("rabbit_hole_next_topic_placeholder")}
              className="min-w-0 flex-1 bg-transparent px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
              data-testid="rabbit-hole-next-topic-input"
            />
            <button
              type="submit"
              disabled={setNextTopic.isPending || !nextTopicDraft.trim()}
              className="flex h-12 shrink-0 items-center justify-center px-4 text-sm font-medium text-slate-700 transition hover:text-slate-950 disabled:text-slate-300"
              data-testid="rabbit-hole-next-topic-save"
            >
              {translate("rabbit_hole_save")}
            </button>
          </div>
        </form>
        {nextTopicSaved ? (
          <p className="mt-2 text-sm text-slate-500">
            {translate("rabbit_hole_next_topic_saved")}
          </p>
        ) : null}
      </section>
    </div>
  );
}

function TopicRefinementCard({
  refinement,
  translate,
  onChooseTopic,
  onKeepBroad,
  testIdPrefix,
}: {
  refinement: RabbitHoleTopicRefinement;
  translate: (key: string, vars?: Record<string, string | number>) => string;
  onChooseTopic: (topic: string) => void | Promise<void>;
  onKeepBroad: () => void | Promise<void>;
  testIdPrefix: string;
}) {
  return (
    <section
      className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4"
      data-testid={`${testIdPrefix}-refinement`}
    >
      <p className="text-sm font-medium text-slate-700">
        {refinement.senseLabel
          ? translate("rabbit_hole_refine_title_sense", {
              topic: refinement.topic,
              sense: refinement.senseLabel,
            })
          : translate("rabbit_hole_refine_title")}
      </p>
      <p className="mt-1 text-sm text-slate-500">
        {translate("rabbit_hole_refine_body", {
          topic: refinement.topic,
          sense: refinement.senseLabel ?? "",
        })}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {refinement.suggestions.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => void onChooseTopic(suggestion)}
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-base text-slate-700 transition hover:bg-slate-100"
            data-testid={`${testIdPrefix}-refinement-option-${suggestion
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "")}`}
          >
            {suggestion}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => void onKeepBroad()}
        className="mt-3 text-sm text-slate-600 underline underline-offset-4 hover:text-slate-900"
        data-testid={`${testIdPrefix}-keep-broad`}
      >
        {translate("rabbit_hole_refine_keep_broad")}
      </button>
    </section>
  );
}

function formatIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
